import {useSelector, useDispatch} from "react-redux";
import {IStoreState} from "modules/types";
import styled from "styled-components";
import {ReactComponent as BallRewardMilestone} from "assets/img/icons/ball_reward_milestone.svg";
import {ReactComponent as FireIcon} from "assets/img/icons/fire.svg";
import {ReactComponent as FireDisabledIcon} from "assets/img/icons/fire_disabled.svg";
import theme from "assets/css/theme";
import headerBanner from "assets/img/headerPicksBanner.png";
import {isMobile} from "modules/utils";
import {Exist} from "components/Exist";
import {IconArrowSlim} from "components/Icons";
import * as React from "react";
import {useEffect, useState, useRef} from "react";
import "intersection-observer";
import {map, findLastIndex, findIndex, throttle} from "lodash";
import {fetchSnapshot, clearSnapshot} from "modules/actions";

const Wrapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	max-width: ${theme.content_width};
	width: 100%;
	overflow: auto;
	scroll-snap-type: x mandatory;
	//no scrollbar
	-ms-overflow-style: none; /* IE 10+ */
	scrollbar-width: none; /* Firefox */

	&::-webkit-scrollbar {
		display: none; /* Safari and Chrome */
	}
`;

const Item = styled.div`
	font-size: 12px;
	line-height: 1.2;
	color: #c7c7cc;
	align-items: center;
	justify-content: center;
	position: relative;
	flex: 1;
	min-width: 55px;
	scroll-snap-align: start;

	--borderColor: transparent;

	p {
		color: #fff;
	}

	&.picked {
		color: #d50a0a;

		&:has(+ .not-picked) {
			.SVGWrapper {
				&:after {
					color: #c7c7cc;
				}
			}
		}

		&.milestone {
			--borderColor: #e28f08;
		}
	}

	&.not-picked {
		& + .picked {
			.SVGWrapper {
				&:before {
					color: #c7c7cc;
				}
			}
		}

		p {
			opacity: 0.4;
		}
	}

	p {
		text-align: center;
	}

	&:first-child {
		.SVGWrapper {
			&:before {
				width: 25%;
				right: 50%;
				left: auto;
				border-top-left-radius: 3px;
				border-bottom-left-radius: 3px;
			}
		}
	}

	&:last-child {
		.SVGWrapper {
			&:after {
				width: 25%;
				left: 50%;
				right: auto;
				border-top-right-radius: 3px;
				border-bottom-right-radius: 3px;
			}
		}
	}
`;

const SVGWrapper = styled.div`
	width: 100%;
	height: 38px;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	z-index: 0;

	&:before {
		content: "";
		width: 50%;
		height: 6px;
		background: currentColor;
		display: block;
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		left: 0;
		z-index: 0;
	}

	&:after {
		content: "";
		width: 50%;
		height: 6px;
		background: currentColor;
		display: block;
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		right: 0;
		z-index: 0;
	}

	svg {
		position: relative;
		z-index: 1;
		width: 38px;
		height: 38px;
	}
`;

const FireWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;
	opacity: 0;
	.milestone & {
		pointer-events: all;
		opacity: 1;
	}
`;

const OuterWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: url("${headerBanner}") no-repeat;
	background-size: 100% 100%;
	color: #fff;
	gap: 16px;
	font-weight: 700;
	font-family: ${theme.base_bold_font};
	padding: 0 16px;
	padding-top: 24px;
	padding-bottom: 16px;
`;

const Header = styled.div`
	display: flex;
	flex-direction: column;
	max-width: ${theme.content_width};
	width: 100%;
	gap: 4px;

	p {
		&:first-child {
			font-size: 24px;
			line-height: 1.5;
		}

		&:last-child {
			font-size: 16px;
			font-family: ${theme.base_font};
		}
	}
`;

const MobileHeader = styled.div`
	display: flex;
	gap: 16px;
	width: 100%;
`;

const ArrowSlimButton = styled.button`
	border: none;
	cursor: pointer;
	background: none;
	color: #fff;
	width: 24px;

	&:focus {
		box-shadow: none;
		border: none;
		outline: none;
	}

	&:disabled {
		opacity: 0.4;
	}
	outline: none;
`;

const MobileHeaderContent = styled.div`
	display: flex;
	flex-direction: column;
	max-width: ${theme.content_width};
	width: 100%;
	gap: 4px;
	font-size: 12px;
	font-family: ${theme.base_font};

	p {
		text-align: center;

		&:first-child {
			font-size: 16px;
			line-height: 1.5;
			font-family: ${theme.base_bold_font};
		}
	}
`;

const WeeksChip = styled.div`
	background: #1b48e0;
	border-radius: 20px;
	padding: 4px 8px;
	display: inline-flex;
	align-self: center;
	margin-bottom: 8px;
	font-size: 12px;
	font-family: ${theme.base_bold_font};
	transition: all 0.1s ease;
`;

export const WeeksPlayed = () => {
	const dispatch = useDispatch();
	const snapshot = useSelector((state: IStoreState) => state.snapshot);
	const gameweeks = useSelector((state: IStoreState) => state.gameweeks);
	const isLoggedIn = useSelector(
		(state: IStoreState) => state.user.is_logged_in
	);
	const [is_mobile, setIsMobile] = useState(isMobile());
	const lineRef = useRef<HTMLDivElement>(null);
	const [visibilityState, setVisibilityState] = useState<boolean[]>([]);
	const [scrollPosition, setScrollPosition] = useState<number>(-1);
	const currentGameWeek = gameweeks.findIndex((it) =>
		["active", "scheduled"].includes(it.status)
	);
	// const currentGameWeek = 4;

	useEffect(() => {
		if (isLoggedIn) {
			dispatch(fetchSnapshot());
		} else {
			dispatch(clearSnapshot());
		}
	}, [isLoggedIn, dispatch]);

	useEffect(() => {
		const onResize = () => {
			setIsMobile(isMobile());
		};
		window.addEventListener("resize", onResize);
		return () => {
			window.removeEventListener("resize", onResize);
		};
	});

	useEffect(() => {
		const observers = map(lineRef.current?.children, (child, index) => {
			const observer = new IntersectionObserver(
				throttle(([entry]) => {
					setVisibilityState((prev) => {
						const newValues = [...prev];
						newValues[index] = entry.isIntersecting;
						return newValues;
					});
				}, 300),
				{
					threshold: 0.5,
				}
			);

			observer.observe(child);

			return () => observer.unobserve(child);
		});

		return () => {
			observers.map((d) => d());
		};
	}, []);

	useEffect(() => {
		const container = lineRef.current;

		if (!container) {
			return;
		}

		const childElem = container.children[currentGameWeek] as HTMLElement;
		container.scrollTo({
			left: childElem?.offsetLeft ?? 0,
		});
	}, [currentGameWeek]);

	const startIndex = findIndex(visibilityState, Boolean) + 1;
	const lastIndex = findLastIndex(visibilityState, Boolean) + 1;

	const onScrollToLeft = () => {
		const leftElement = lineRef.current?.children[startIndex];
		if (leftElement && lineRef.current) {
			const width = lineRef.current.clientWidth;
			const currentScrollLeft = lineRef.current.scrollLeft;

			lineRef.current.scrollTo({
				behavior: "smooth",
				left: Math.max(
					currentScrollLeft - width + leftElement.clientWidth,
					0
				),
			});
		}
	};

	const onScrollToRight = () => {
		const rightElement = lineRef.current?.children[lastIndex];
		if (rightElement && lineRef.current) {
			const width = lineRef.current.clientWidth;
			const currentScrollLeft = lineRef.current.scrollLeft;
			console.log(currentScrollLeft, width, rightElement.clientWidth);
			lineRef.current.scrollTo({
				behavior: "smooth",
				left: Math.max(
					currentScrollLeft + width - rightElement.clientWidth,
					0
				),
				top: 0,
			});
		}
	};

	const checkDisabled = () => {
		const container = lineRef.current;

		if (!container) {
			return;
		}
		if (container.scrollLeft === 0) {
			setScrollPosition(-1);
			return;
		}

		if (container.scrollLeft > container.clientWidth - 60) {
			setScrollPosition(1);
			return;
		}

		setScrollPosition(0);
	};

	const titleCopy = window.getTranslations(
		"streak.title", // My Season
		[],
		"My Season"
	);

	const descriptionCopy = window.getTranslations(
		"streak.description", // Make your weekly picks to build your streak!
		[],
		"Make your weekly picks to build your streak!"
	);

	const weekTranslation = window.getTranslations(
		"streak.week", // Week,
		[],
		"Week"
	);

	const weekLetterTranslation = window.getTranslations(
		"streak.week_letter", // W,
		[],
		"W"
	);

	return (
		<OuterWrapper>
			<Exist when={!is_mobile}>
				<Header>
					<p>{titleCopy}</p>
					<p>{descriptionCopy}</p>
				</Header>
			</Exist>

			<Exist when={is_mobile}>
				<MobileHeader>
					<ArrowSlimButton
						onClick={onScrollToLeft}
						disabled={scrollPosition === -1}
					>
						<IconArrowSlim color="#ffffff" to="left" width={12} />
					</ArrowSlimButton>

					<MobileHeaderContent>
						<p>{titleCopy}</p>
						<WeeksChip>
							{weekTranslation} {startIndex} - {lastIndex}
						</WeeksChip>
						<p>{descriptionCopy}</p>
					</MobileHeaderContent>

					<ArrowSlimButton
						onClick={onScrollToRight}
						disabled={scrollPosition === 1}
					>
						<IconArrowSlim color="#ffffff" to="right" width={12} />
					</ArrowSlimButton>
				</MobileHeader>
			</Exist>

			<Wrapper ref={lineRef} onScroll={checkDisabled}>
				{gameweeks.map((it, index) => {
					const isPicked = snapshot?.picked_rounds
						? snapshot.picked_rounds.includes(it.id)
						: false;
					const isMilestone = !!index && !((index + 1) % 3);
					return (
						<Item
							key={it.id}
							className={`
							${isPicked ? "picked" : "not-picked"} ${isMilestone ? "milestone" : ""}`}
						>
							<FireWrapper>
								<Exist when={!isPicked}>
									<FireDisabledIcon />
								</Exist>
								<Exist when={isPicked}>
									<FireIcon />
								</Exist>
							</FireWrapper>
							<SVGWrapper className={`SVGWrapper`}>
								<BallRewardMilestone />
							</SVGWrapper>

							<p>
								{weekLetterTranslation}
								{it.id}
							</p>
						</Item>
					);
				})}
			</Wrapper>
		</OuterWrapper>
	);
};
